import type { BurntModelInfo } from "@/api/custom-schema";
import { cn } from "@/lib/utils";
import { useLocalization } from "@/context/localization-context";

interface BurntModelViewProps {
  model: BurntModelInfo;
  className?: string;
}

/**
 * Burnt verdict of the crust-strip CNN: p(NOK) against the decision threshold
 * plus the provenance the backend records (model version, TTA, timing, strip).
 * The heatmap layer is the model's Grad-CAM response projected onto the crust
 * (empty for confident OK pizzas), see vision/burnt_cnn.py in the backend.
 */
interface OutputGaugeProps {
  label: string;
  probability: number;
  threshold: number;
  positiveLabel: string;
  positiveClass: string;
  barClass: string;
}

/** One sigmoid output of the model: probability against its threshold, verdict badge, bar. */
function OutputGauge({
  label,
  probability,
  threshold,
  positiveLabel,
  positiveClass,
  barClass,
}: OutputGaugeProps) {
  const { t, formatNumber } = useLocalization();
  const p = Math.min(1, Math.max(0, probability));
  const th = Math.min(1, Math.max(0, threshold));
  const positive = p >= th;
  const margin = Math.abs(p - th);
  const certainty =
    margin < 0.1
      ? t("detail.burntModel.borderline")
      : margin < 0.25
        ? t("detail.burntModel.moderate")
        : t("detail.burntModel.confident");
  const percentText = (value: number, digits: number) =>
    formatNumber(value, { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-3xl font-bold tabular-nums">
            {percentText(p, 1)}
          </div>
        </div>
        <div className="text-right">
          <div
            className={cn(
              "inline-block rounded-md px-3 py-1 text-sm font-semibold text-white",
              positive ? positiveClass : "bg-green-600",
            )}
          >
            {positive ? positiveLabel : t("status.ok")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t("detail.burntModel.certainty", { certainty, threshold: percentText(th, 0) })}
          </div>
        </div>
      </div>

      {/* probability bar with the threshold marker */}
      <div className="relative h-3 w-full rounded-full bg-muted">
        <div
          className={cn("h-3 rounded-full", positive ? barClass : "bg-green-500")}
          style={{ width: `${p * 100}%` }}
        />
        <div
          className="absolute top-[-4px] h-5 w-0.5 bg-foreground"
          style={{ left: `calc(${th * 100}% - 1px)` }}
          title={t("detail.gauge.threshold", { value: percentText(th, 0) })}
        />
      </div>
    </div>
  );
}

export function BurntModelView({ model, className }: BurntModelViewProps) {
  const { t, formatNumber } = useLocalization();
  const hasRaw =
    typeof model.raw_probability === "number" &&
    Number.isFinite(model.raw_probability);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <OutputGauge
        label={t("detail.burntModel.pBurnt")}
        probability={model.probability}
        threshold={model.threshold}
        positiveLabel={t("verdict.check.burnt")}
        positiveClass="bg-red-600"
        barClass="bg-red-500"
      />
      {hasRaw && (
        <OutputGauge
          label={t("detail.burntModel.pRaw")}
          probability={model.raw_probability as number}
          threshold={model.raw_threshold ?? 0.5}
          positiveLabel={t("verdict.check.raw")}
          positiveClass="bg-sky-600"
          barClass="bg-sky-500"
        />
      )}

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t("detail.panel.model")}</dt>
        <dd className="font-mono">
          {model.name} {model.version}
          {model.axes && model.axes.length > 1 && (
            <span className="ml-2 text-muted-foreground">
              [{model.axes.join(", ")}]
            </span>
          )}
        </dd>
        {model.tta != null && (
          <>
            <dt className="text-muted-foreground">{t("detail.burntModel.tta")}</dt>
            <dd className="tabular-nums">{t("detail.burntModel.ttaValue", { count: model.tta })}</dd>
          </>
        )}
        {model.inference_ms != null && (
          <>
            <dt className="text-muted-foreground">{t("detail.burntModel.inference")}</dt>
            <dd className="tabular-nums">
              {formatNumber(model.inference_ms, { maximumFractionDigits: 0 })} ms (CPU)
            </dd>
          </>
        )}
        {model.crust_fraction != null && (
          <>
            <dt className="text-muted-foreground">{t("detail.burntModel.crustInStrip")}</dt>
            <dd className="tabular-nums">
              {formatNumber(model.crust_fraction, { style: "percent", maximumFractionDigits: 0 })}
              {model.crust_fraction < 0.25 && (
                <span className="ml-2 text-amber-600">{t("detail.burntModel.lowCrust")}</span>
              )}
            </dd>
          </>
        )}
        {model.strip && (
          <>
            <dt className="text-muted-foreground">{t("detail.burntModel.polarStrip")}</dt>
            <dd className="tabular-nums">
              {model.strip.width}×{model.strip.height}, r {model.strip.r_lo}–
              {model.strip.r_hi} R
            </dd>
          </>
        )}
      </dl>
      <p className="text-xs text-muted-foreground">
        {hasRaw ? t("detail.burntModel.explainTwoOutputs") : t("detail.burntModel.explainOneOutput")}
      </p>
    </div>
  );
}

export default BurntModelView;
